// CodePastePlugin — handles paste for code content.
//
// Two scenarios:
// 1. Paste inside an existing CodeNode — forces plain text so lines stay
//    inside the single code block instead of creating separate paragraphs.
// 2. Paste from VS Code (or other IDEs) into a regular paragraph — detects
//    the VS Code HTML signature and auto-wraps into a single CodeNode.
//
// Without this plugin, VS Code's rich HTML clipboard creates dozens of
// separate paragraph/code nodes instead of one unified code block.

import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createLineBreakNode,
  $insertNodes,
  $getRoot,
  $isParagraphNode,
  PASTE_TAG,
  type LexicalNode,
} from 'lexical';
import {
  $isCodeNode,
  $createCodeHighlightNode,
  $createCodeNode,
  type CodeNode,
} from '@lexical/code';
import { mergeRegister } from '@lexical/utils';

/**
 * Detect whether the clipboard HTML originates from VS Code or another IDE.
 * VS Code wraps copied code in a <div> with style="..." containing
 * font-family references and data-vscode attributes, or uses specific
 * class patterns. We check a few reliable signals.
 */
function isVSCodeHtml(html: string): boolean {
  if (!html) return false;
  const normalized = html.toLowerCase();
  // VS Code specific attributes / markers
  if (normalized.includes('data-vscode')) return true;
  if (normalized.includes('vscode-')) return true;
  if (normalized.includes('class="mtk') || normalized.includes("class='mtk")) return true;
  // VS Code wraps in a div with inline styles containing monospace font
  if (normalized.includes('font-family') && normalized.includes('monospace')) return true;
  // JetBrains IDEs use a similar pattern
  if (normalized.includes('jetbrains') || normalized.includes('intellij')) return true;
  // Monaco/IDE rich HTML usually contains dense color spans per token
  if (
    normalized.includes('<span') &&
    normalized.includes('color:') &&
    normalized.includes('background-color')
  ) {
    return true;
  }
  // Generic: if the HTML is a single <pre> or contains <pre><code>
  const trimmed = normalized.trim();
  if (trimmed.startsWith('<pre') || trimmed.includes('<pre><code')) return true;
  return false;
}

function hasVSCodeClipboardMimeType(dataTransfer: DataTransfer): boolean {
  const types = Array.from(dataTransfer.types ?? []).map((type) => type.toLowerCase());
  return types.some(
    (type) =>
      type === 'vscode-editor-data' ||
      type === 'application/vnd.code.copymetadata' ||
      type.includes('vscode'),
  );
}

type PastePayload = ClipboardEvent | InputEvent | KeyboardEvent;
type PasteEventKind = 'clipboard' | 'input' | 'keyboard';

function getDataTransfer(payload: PastePayload): DataTransfer | null {
  if ('clipboardData' in payload && payload.clipboardData) {
    return payload.clipboardData;
  }
  if ('dataTransfer' in payload && payload.dataTransfer) {
    return payload.dataTransfer;
  }
  return null;
}

function getPayloadKind(payload: PastePayload): PasteEventKind {
  if ('clipboardData' in payload) return 'clipboard';
  if ('dataTransfer' in payload) return 'input';
  return 'keyboard';
}

function isVSCodeClipboard(dataTransfer: DataTransfer, html: string): boolean {
  return hasVSCodeClipboardMimeType(dataTransfer) || isVSCodeHtml(html);
}

/**
 * Populate a CodeNode with plain text lines as CodeHighlightNode children.
 */
function fillCodeNodeWithText(codeNode: ReturnType<typeof $createCodeNode>, text: string): void {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  // Remove trailing empty line (common when copying full lines)
  if (lines.length > 1 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) {
      codeNode.append($createLineBreakNode());
    }
    if (lines[i].length > 0) {
      codeNode.append($createCodeHighlightNode(lines[i]));
    }
  }
}

function mergeAdjacentCodeNodes(): boolean {
  let didMerge = false;
  let current = $getRoot().getFirstChild();

  while (current) {
    if (!$isCodeNode(current)) {
      current = current.getNextSibling();
      continue;
    }

    let next = current.getNextSibling();
    while (next) {
      if ($isParagraphNode(next) && next.getTextContent().trim() === '') {
        const emptyParagraph = next;
        next = next.getNextSibling();
        emptyParagraph.remove();
        didMerge = true;
        continue;
      }

      if (!$isCodeNode(next)) {
        break;
      }

      if (current.getChildrenSize() > 0 && next.getChildrenSize() > 0) {
        current.append($createLineBreakNode());
      }
      const children = next.getChildren();
      if (children.length > 0) {
        current.append(...children);
      }

      const mergedSibling = next;
      next = next.getNextSibling();
      mergedSibling.remove();
      didMerge = true;
    }

    current = next;
  }

  return didMerge;
}

function isEmptyParagraphNode(node: LexicalNode): boolean {
  return $isParagraphNode(node) && node.getTextContent().trim() === '';
}

function flattenNestedCodeText(codeNode: CodeNode): string {
  const children = codeNode.getChildren();
  const childCodeNodes = children.filter((child): child is CodeNode => $isCodeNode(child));
  if (childCodeNodes.length === 0 || childCodeNodes.length !== children.length) {
    return codeNode.getTextContent();
  }
  return childCodeNodes.map((childCodeNode) => flattenNestedCodeText(childCodeNode)).join('\n');
}

function getClipboardCodeNormalizationText(nodes: LexicalNode[]): string | null {
  const significantNodes = nodes.filter((node) => !isEmptyParagraphNode(node));
  if (significantNodes.length === 0) {
    return null;
  }

  if (significantNodes.length === 1 && $isCodeNode(significantNodes[0])) {
    const singleCodeNode = significantNodes[0];
    const hasNestedCodeChildren = singleCodeNode
      .getChildren()
      .some((child) => $isCodeNode(child));

    if (!hasNestedCodeChildren) {
      return null;
    }

    return flattenNestedCodeText(singleCodeNode);
  }

  if (!significantNodes.every((node) => $isCodeNode(node))) {
    return null;
  }

  return significantNodes
    .map((node) => node.getTextContent().replace(/\r\n/g, '\n'))
    .join('\n');
}

function getEventTimestamp(payload: PastePayload): number {
  return typeof payload.timeStamp === 'number' ? payload.timeStamp : Date.now();
}

export default function CodePastePlugin(): null {
  const [editor] = useLexicalComposerContext();
  const lastHandledPasteRef = useRef<{
    kind: PasteEventKind;
    text: string;
    timestamp: number;
  } | null>(null);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
        (payload: { nodes: LexicalNode[]; selection: ReturnType<typeof $getSelection> }) => {
          const { nodes, selection } = payload;
          if (!$isRangeSelection(selection)) {
            return false;
          }

          const normalizedCodeText = getClipboardCodeNormalizationText(nodes);
          if (!normalizedCodeText || normalizedCodeText.trim().length === 0) {
            return false;
          }

          const codeNode = $createCodeNode();
          fillCodeNodeWithText(codeNode, normalizedCodeText);

          selection.removeText();
          selection.insertNodes([codeNode]);
          codeNode.selectEnd();
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (payload: PastePayload) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;

          const dataTransfer = getDataTransfer(payload);
          if (!dataTransfer) return false;

          const plainText = dataTransfer.getData('text/plain');
          if (!plainText) return false;

          const now = getEventTimestamp(payload);
          const kind = getPayloadKind(payload);
          const lastHandled = lastHandledPasteRef.current;
          const isDuplicate =
            !!lastHandled &&
            lastHandled.kind !== kind &&
            lastHandled.text === plainText &&
            Math.abs(lastHandled.timestamp - now) < 120;

          if (isDuplicate) {
            payload.preventDefault();
            return true;
          }

          const anchor = selection.anchor.getNode();
          const anchorParent = anchor.getParent();
          const isInsideCode = $isCodeNode(anchor) || (anchorParent && $isCodeNode(anchorParent));

          // --- Case 1: Already inside a CodeNode ---
          if (isInsideCode) {
            payload.preventDefault();

            const lines = plainText.replace(/\r\n/g, '\n').split('\n');
            selection.removeText();

            for (let i = 0; i < lines.length; i++) {
              if (i > 0) {
                selection.insertNodes([$createLineBreakNode()]);
              }
              if (lines[i].length > 0) {
                selection.insertNodes([$createCodeHighlightNode(lines[i])]);
              }
            }

            lastHandledPasteRef.current = { kind, text: plainText, timestamp: now };
            return true;
          }

          // --- Case 2: Paste from VS Code / IDE into regular paragraph ---
          const htmlData = dataTransfer.getData('text/html');
          if (!isVSCodeClipboard(dataTransfer, htmlData)) return false;

          const lineCount = plainText.replace(/\r\n/g, '\n').split('\n').length;
          if (lineCount < 2) return false; // Single line — let default handle it

          payload.preventDefault();

          const codeNode = $createCodeNode();
          fillCodeNodeWithText(codeNode, plainText);

          selection.removeText();
          $insertNodes([codeNode]);
          codeNode.selectEnd();

          lastHandledPasteRef.current = { kind, text: plainText, timestamp: now };
          return true;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerUpdateListener(({ tags }) => {
        // Fallback normalization: if IDE HTML still produced fragmented CodeNodes,
        // merge adjacent siblings into one block right after paste.
        if (!tags.has(PASTE_TAG) || tags.has('code-paste-normalize')) {
          return;
        }

        editor.update(
          () => {
            mergeAdjacentCodeNodes();
          },
          { tag: 'code-paste-normalize' },
        );
      }),
    );
  }, [editor]);

  return null;
}
